import type { FC, ReactNode } from 'react'

import { useEffect, useRef, useState } from 'react'
import { Gift, Heart } from 'lucide-react'
import { cn } from '@/lib/utils'
import { heartBurst } from '@/lib/confetti'
import { useLockScroll } from '@/hooks/use-lock-scroll'
import FloatingHearts from '@/components/landing/floating-hearts'
import { GATE, HERO } from '@/constants/birthday'

interface SurpriseGateProps {
    onOpen: () => void
}

type Phase = 'locked' | 'opening' | 'leaving' | 'gone'

// Envelope screen that hides the whole page until she taps to open it.
const SurpriseGate: FC<SurpriseGateProps> = ({ onOpen }): ReactNode => {
    const [phase, setPhase] = useState<Phase>('locked')
    const timers = useRef<number[]>([])

    useLockScroll(phase !== 'gone')

    useEffect(() => {
        const pending = timers.current

        return () => pending.forEach((timer) => window.clearTimeout(timer))
    }, [])

    const open = (): void => {
        if (phase !== 'locked') return

        const quick = window.matchMedia('(prefers-reduced-motion: reduce)').matches
        const later = (callback: () => void, delay: number): void => {
            timers.current.push(window.setTimeout(callback, quick ? 0 : delay))
        }

        setPhase('opening')

        later(() => {
            void heartBurst({ x: 0.5, y: 0.45 })
        }, 550)

        later(() => {
            if (!window.location.hash) window.scrollTo({ top: 0, behavior: 'instant' })

            setPhase('leaving')
            onOpen()
        }, 1200)

        later(() => setPhase('gone'), 1900)
    }

    if (phase === 'gone') return null

    const opening = phase !== 'locked'

    return (
        <div
            role='dialog'
            aria-modal='true'
            aria-labelledby='gate-title'
            className={cn(
                'fixed inset-0 z-50 overflow-y-auto overscroll-contain bg-background transition duration-700 motion-reduce:transition-none',
                phase === 'leaving' && 'pointer-events-none scale-105 opacity-0'
            )}
        >
            <div aria-hidden className='pointer-events-none absolute inset-0 overflow-hidden'>
                <div className='absolute -left-24 -top-24 size-80 rounded-full bg-accent blur-3xl' />
                <div className='absolute -bottom-32 -right-20 size-96 rounded-full bg-primary/15 blur-3xl' />
                <div className='absolute left-1/2 top-1/3 size-72 -translate-x-1/2 rounded-full bg-gold/20 blur-3xl' />
            </div>

            <FloatingHearts className='text-primary' />

            <div className='relative flex min-h-full flex-col items-center justify-center px-6 py-14 text-center'>
                <h2 id='gate-title' className='font-script text-6xl leading-tight text-primary sm:text-7xl'>
                    {GATE.title}
                </h2>

                <p className='mt-3 max-w-sm text-lg font-semibold text-foreground/80 text-pretty sm:text-xl'>
                    {GATE.tagline}
                </p>

                <div
                    aria-hidden
                    onClick={open}
                    className={cn('mt-12 w-[min(78vw,20rem)]', !opening && 'cursor-pointer')}
                >
                    <div className='relative aspect-[4/3] perspective-[1100px]'>
                        <div className='absolute inset-0 rounded-2xl bg-rose-200 shadow-2xl shadow-primary/25' />

                        <div
                            className={cn(
                                'absolute inset-x-4 bottom-3 top-3 z-10 flex flex-col items-center justify-center rounded-xl bg-paper shadow-md transition-transform delay-300 duration-700 ease-out motion-reduce:transition-none',
                                opening && '-translate-y-[55%]'
                            )}
                        >
                            <Heart className='size-7 fill-primary text-primary' />
                            <span className='mt-1 font-script text-4xl text-primary'>
                                {HERO.name}
                            </span>
                        </div>

                        <div className='absolute inset-0 z-20 rounded-2xl bg-rose-300 [clip-path:polygon(0_0,50%_58%,100%_0,100%_100%,0_100%)]' />

                        <div
                            className={cn(
                                'absolute inset-x-0 top-0 h-[60%] origin-top bg-rose-400 transition-transform duration-500 ease-in-out [clip-path:polygon(0_0,100%_0,50%_100%)] motion-reduce:transition-none',
                                opening ? 'z-0 rotate-x-180' : 'z-30'
                            )}
                        />

                        <span
                            className={cn(
                                'absolute left-1/2 top-[58%] z-40 grid size-14 -translate-x-1/2 -translate-y-1/2 place-items-center rounded-full bg-primary text-primary-foreground shadow-lg ring-4 ring-rose-100 transition duration-300',
                                opening ? 'scale-0 opacity-0' : 'animate-heartbeat motion-reduce:animate-none'
                            )}
                        >
                            <Heart className='size-6 fill-current' />
                        </span>
                    </div>
                </div>

                <button
                    type='button'
                    onClick={open}
                    disabled={opening}
                    className='mt-14 inline-flex items-center gap-2.5 rounded-full bg-primary px-8 py-4 text-base font-bold text-primary-foreground shadow-xl shadow-primary/30 transition hover:-translate-y-0.5 hover:shadow-2xl focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-primary/40 disabled:opacity-70'
                >
                    <Gift className='size-5' />
                    {GATE.button}
                </button>
            </div>
        </div>
    )
}

export default SurpriseGate
